import classNames from "classnames";
import creditCardType from "credit-card-type";
import React from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import iconAmericanExpress from "../../../../img/checkout/american-express.png";
import iconCCBack from "../../../../img/checkout/cc-back.png";
import creditCards from "../../../../img/checkout/credit-cards.png";
import iconDiscover from "../../../../img/checkout/discover.png";
import iconLock from "../../../../img/checkout/lock.png";
import iconMastercard from "../../../../img/checkout/mastercard.png";
import iconVisa from "../../../../img/checkout/visa.png";
import { Image } from "../../../common/Image";
import { FormCreditCardNumberInput } from "../../../forms/FormCreditCardNumberInput";
import { FormInput } from "../../../forms/FormInput";
import { FormNumber } from "../../../forms/FormNumber";
import { format } from "../../../utils/format";
import { CARD_TYPE_VISA } from "../constants";
import { ICybersourcePayment, IPayment } from "../reducers.interfaces";
import { IPaymentMethodFormProps } from "./PaymentMethod.interfaces";

const cardIcons = {
    "american-express": iconAmericanExpress,
    "mastercard": iconMastercard,
    "visa": iconVisa,
    "discover": iconDiscover,
    "lock": iconLock,
};

const cardIconExists = (
    cardType: string,
): cardType is keyof typeof cardIcons => {
    return Object.prototype.hasOwnProperty.call(cardIcons, cardType);
};

const getCardIcon = (cardType: string): string | null => {
    return cardIconExists(cardType) ? cardIcons[cardType] : null;
};

const getMethodData = (
    form: IPaymentMethodFormProps["form"],
    methodKey: string,
): ICybersourcePayment => {
    const data = form.payment_methods[methodKey];
    let cybData: ICybersourcePayment | null = null;
    if (data.method_type === "cybersource") {
        cybData = data;
    }
    const baseFields: ICybersourcePayment = {
        method_type: "cybersource",
        amount: "",
        pay_balance: false,
        card_type: CARD_TYPE_VISA,
        card_number: "",
        card_expiration: "",
        card_cvc: "",
    };
    return {
        ...baseFields,
        ...cybData,
    };
};

interface IProps extends IPaymentMethodFormProps {
    setPaymentMethodFields: (
        methodKey: string,
        fields: Partial<IPayment>,
    ) => void;
    changeCreditCardNumber: (methodKey: string, cardNumber: string) => void;
    changeCreditCardExpDate: (methodKey: string, expDate: string) => void;
    changeAmount: (methodKey: string, amount: string) => void;
}

export const Cybersource = (props: IProps): React.ReactNode => {
    const cvcElemRef = React.useRef<HTMLInputElement>(null);

    const onCardNumberChange = (event: React.FormEvent<HTMLInputElement>) => {
        props.changeCreditCardNumber(
            props.methodKey,
            event.currentTarget.value,
        );
    };

    const onCardExpChange = (event: React.FormEvent<HTMLInputElement>) => {
        props.changeCreditCardExpDate(
            props.methodKey,
            event.currentTarget.value,
        );
    };

    const onChange = (event: React.FormEvent<HTMLInputElement>) => {
        props.setPaymentMethodFields(props.methodKey, {
            [event.currentTarget.name]: event.currentTarget.value,
        });
    };

    const onAmountChange = (event: React.FormEvent<HTMLInputElement>) => {
        props.changeAmount(props.methodKey, event.currentTarget.value);
    };

    const onValidStateChange = (fieldName: string, errorMessages: string[]) => {
        props.onValidStateChange(fieldName, errorMessages, props.methodKey);
    };

    const methodData = getMethodData(props.form, props.methodKey);
    const cardNumber = methodData.card_number.replace(/\s+/g, "");
    const types = creditCardType(cardNumber);
    const type = types[0];

    const cardNumberClasses = classNames({
        ["form__card-number"]: true,
    });
    const cardIcon = getCardIcon(
        type && cardNumber.length ? type.type : "lock",
    );

    const securityCode =
        type && type.code
            ? type.code
            : {
                  name: "CVV",
                  size: 3,
              };

    const errors =
        props.methodKey in props.errors ? props.errors[props.methodKey] : {};

    const creditCardImage = creditCards;
    const sectionName = props.header
        ? `_${props.header.replace(/ +/g, "_").toLowerCase()}`
        : "";

    return (
        <div>
            <h4 className="checkout-step__bar-heading">{props.header}</h4>
            <div className="checkout-step__form-container checkout-step__form-container--cc checkout-step__form-container--blue">
                <div className="checkout-step__form-header">
                    <div className="checkout-step__form-header--cc-copy">
                        {/* TODO(i18n): Wrap in t tagged template: {t`Secure Credit Card Payment`} */}
                        <h5 className="checkout-step__copy--bold">
                            Secure Credit Card Payment
                        </h5>
                        {/* TODO(i18n): Wrap in t tagged template: {t`Use your VISA, MasterCard, American Express or Discover credit card.`} */}
                        <div>
                            Use your VISA, MasterCard, American Express or
                            Discover credit card.
                        </div>
                    </div>
                    <div className="checkout-step__form-header--cc-image">
                        {/* TODO(i18n): Wrap alt text in t tagged template: alt={t`Accepted Credit Cards: VISA, MasterCard, American Express or Discover`} */}
                        <Image
                            src={creditCardImage}
                            className="checkout-step__form-image--cc responsive-img"
                            alt="Accepted Credit Cards: VISA, MasterCard, American Express or Discover"
                        />
                    </div>
                </div>
                <fieldset disabled={props.disabled} style={{ border: 0 }}>
                    <legend className="ada-screenreader-only">
                        {/* TODO(i18n): Translate fallback string inline: !props.header ? t`Credit Card` : props.header */}
                        {interpolate(t`%s Information`, [
                            `${!props.header ? "Credit Card" : props.header}`,
                        ])}
                    </legend>
                    {/* TODO(i18n): Wrap in t tagged template: {t`Card Number`} */}
                    <label
                        htmlFor={`card_number${sectionName}`}
                        className="checkout-step__copy--bold"
                    >
                        Card Number
                    </label>
                    <FormCreditCardNumberInput
                        name="card_number"
                        id={`card_number${sectionName}`}
                        showErrorMessages={props.showErrorMessages}
                        onValidStateChange={onValidStateChange}
                        className={cardNumberClasses}
                        value={methodData.card_number}
                        errors={errors.card_number}
                        onChange={onCardNumberChange}
                        secureImage={
                            cardIcon
                                ? {
                                      image: cardIcon,
                                      alt: t`Secure`,
                                  }
                                : undefined
                        }
                    />
                    <div className="checkout-step__payment-cc-expiration">
                        {/* TODO(i18n): Wrap in t tagged template: {t`Expiration (MM/YY)`} */}
                        <label
                            htmlFor={`card_expiration${sectionName}`}
                            className="checkout-step__copy--bold"
                        >
                            Expiration (MM/YY)
                        </label>
                        <FormInput
                            type="text"
                            name="card_expiration"
                            id={`card_expiration${sectionName}`}
                            validation={["required", "expiration-date"]}
                            showErrorMessages={props.showErrorMessages}
                            onValidStateChange={onValidStateChange}
                            autoComplete="cc-exp"
                            value={methodData.card_expiration}
                            errors={errors.card_expiration}
                            onChange={onCardExpChange}
                            formatOptions={{
                                type: "date",
                                datePattern: ["m", "y"],
                            }}
                            maxLength={5}
                        />
                    </div>
                    <div className="checkout-step__payment-cc-code">
                        {/* TODO(i18n): Wrap in t tagged template: {t`Security Code`} */}
                        <label
                            htmlFor={`card_cvc${sectionName}`}
                            className="checkout-step__copy--bold"
                        >
                            Security Code
                        </label>
                        <FormInput
                            inputRef={cvcElemRef}
                            type="text"
                            name="card_cvc"
                            id={`card_cvc${sectionName}`}
                            autoComplete="cc-csc"
                            validation={[
                                "required",
                                securityCode.size === 3 ? "cvc3" : "cvc4",
                            ]}
                            showErrorMessages={props.showErrorMessages}
                            onValidStateChange={onValidStateChange}
                            value={methodData.card_cvc}
                            errors={errors.card_cvc}
                            onChange={onChange}
                            formatOptions={{
                                type: "general",
                                numericOnly: true,
                                blocks: [securityCode.size],
                            }}
                            secureImage={{
                                image: iconCCBack,
                                alt: t`Code on back of card, Secure`,
                            }}
                            maxLength={securityCode.size}
                        />
                    </div>
                    {props.isSplitPay && (
                        <div className="checkout-step__payment-amount">
                            {/* TODO(i18n): Wrap in t tagged template: {t`Amount`} */}
                            <label
                                htmlFor={`amount${sectionName}`}
                                className="checkout-step__copy--bold"
                            >
                                Amount
                            </label>
                            <FormNumber
                                name="amount"
                                id={`amount${sectionName}`}
                                autoComplete=""
                                step={0.01}
                                validation={["required", "amount", "nonzero"]}
                                showErrorMessages={props.showErrorMessages}
                                onValidStateChange={onValidStateChange}
                                value={methodData.amount || undefined}
                                errors={errors.amount}
                                onChange={onAmountChange}
                                disabled={props.shouldFreezeAmount}
                            />
                        </div>
                    )}
                    {props.disabled && (
                        <div>
                            <h5>{t`Pre-Authorized`}</h5>
                            <p>
                                {interpolate(
                                    t`This card has been pre-authorized for the amount of %s`,
                                    [format.money(methodData.amount || "0.00")],
                                )}
                                .
                            </p>
                        </div>
                    )}
                </fieldset>
            </div>
        </div>
    );
};
